import { useState } from 'react'

import { useQuery } from '@tanstack/react-query'
import { Building2, Check, ChevronDown, FolderKanban, Plus } from 'lucide-react'
import { useLocation, useNavigate } from 'react-router-dom'

import { CreateProjectModal } from '@/features/projects/components/create-project-modal'
import { listProjects } from '@/features/projects/projects-api'
import { projectsQueryKey } from '@/features/projects/projects-query-keys'
import { isProjectSection, type ProjectSection } from '@/features/projects/project-routes'
import { DropdownItem, DropdownMenu } from '@/shared/components/ui/dropdown-menu'
import { cn } from '@/shared/utils/cn'

function parseProjectLocation(pathname: string): {
  projectId: string | null
  section: ProjectSection
} {
  const parts = pathname.split('/')

  if (parts[1] !== 'projects' || !parts[2]) {
    return { projectId: null, section: 'overview' }
  }

  const section = parts[3] && isProjectSection(parts[3]) ? parts[3] : 'overview'

  return { projectId: parts[2], section }
}

export function ProjectSwitcher() {
  const location = useLocation()
  const navigate = useNavigate()
  const [createOpen, setCreateOpen] = useState(false)

  const projectsQuery = useQuery({ queryKey: projectsQueryKey, queryFn: () => listProjects() })
  const { projectId, section } = parseProjectLocation(location.pathname)
  const currentProject = projectsQuery.data?.find((project) => project.id === projectId) || null

  return (
    <>
      <DropdownMenu
        align="left"
        trigger={
          <span
            className={cn(
              'flex max-w-[220px] items-center gap-2 rounded-lg border px-3 py-1.5 text-sm font-medium transition hover:bg-[var(--panel-subtle)]',
              currentProject
                ? 'border-[var(--color-accent)] text-[var(--color-accent)]'
                : 'border-[var(--panel-border)] text-[var(--color-text-soft)]',
            )}
          >
            <span className="truncate">{currentProject?.name || 'Выберите проект'}</span>
            <ChevronDown className="h-4 w-4 flex-none" />
          </span>
        }
      >
        {(close) => (
          <div className="min-w-[240px]">
            <DropdownItem
              icon={<FolderKanban className="h-4 w-4" />}
              onClick={() => {
                close()
                navigate('/projects')
              }}
            >
              Все проекты
            </DropdownItem>
            {(projectsQuery.data || []).length > 0 ? (
              <div className="my-1 border-t border-[var(--panel-border)]" />
            ) : null}
            {(projectsQuery.data || []).map((project) => (
              <DropdownItem
                icon={<Building2 className="h-4 w-4" />}
                key={project.id}
                onClick={() => {
                  close()
                  // Сохраняем текущий раздел; если у целевого проекта раздел
                  // недоступен (widget выключен) — WidgetSection уведёт на overview.
                  navigate(`/projects/${project.id}/${section}`)
                }}
              >
                <span className="flex w-full items-center justify-between gap-2">
                  <span className="truncate">{project.name}</span>
                  {project.id === projectId ? (
                    <Check className="h-4 w-4 flex-none text-[var(--color-accent)]" />
                  ) : null}
                </span>
              </DropdownItem>
            ))}
            <div className="my-1 border-t border-[var(--panel-border)]" />
            <DropdownItem
              icon={<Plus className="h-4 w-4 text-[var(--color-accent)]" />}
              onClick={() => {
                close()
                setCreateOpen(true)
              }}
            >
              Новый проект
            </DropdownItem>
          </div>
        )}
      </DropdownMenu>

      <CreateProjectModal
        onClose={() => setCreateOpen(false)}
        onCreated={(project) => navigate(`/projects/${project.id}/overview`)}
        open={createOpen}
      />
    </>
  )
}
